import { EventEmitter } from 'events';
import { SbLoggerI } from './types';

const RECONNECT_DELAY = 1000;
const RECONNECT_DELAY_MAX = 20000;
const PONG_FREQUENCY = 40000;
export const ACTION_RESPONSE_TIMEOUT = 5000;

// codes: https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
const WS_CLOSE_CODE = {
  NORMAL: 1000,
  GOING_AWAY: 1001,
  PROTOCOL_ERROR: 1002,
  NO_STATUS: 1005,
  ABNORMAL: 1006,
  INTERNAL_ERROR: 1011,
  BAD_GATEWAY: 1014,
};

export enum SbSocketErrorCode {
  /**
   * Socket disconnected locally by js client (not server)
   */
  CLIENT_DISCONNECT = 'CLIENT_DISCONNECT',

  /**
   * Socket connection failed during handshake and never opened.
   */
  CONNECT_REJECTED = 'CONNECT_REJECTED',

  /**
   * Visitor is blocked.
   */
  BLOCKED = 'BLOCKED',

  /**
   * Max sessions
   */
  MAX_SESSIONS = 'MAX_SESSIONS',

  /**
   * The widget's protocol is ahead of the server's.
   */
  PROTOCOL_VERSION_AHEAD = 'PROTOCOL_VERSION_AHEAD',

  /**
   * The widget's protocol is behind the server's.
   */
  PROTOCOL_VERSION_BEHIND = 'PROTOCOL_VERSION_BEHIND',

  /**
   * The same user connected from a different browser context
   * and kicked this socket connection.
   */
  DISPLACED = 'DISPLACED',

  /**
   * A connection attempt failed due to being offline.
   */
  OFFLINE = 'OFFLINE',

  /**
   * Unknown socket error.
   */
  UNKNOWN = 'UNKNOWN',

  /**
   * There was a problem with the account
   */
  BAD_ACCOUNT = 'BAD_ACCOUNT',

  /**
   * This Agent User has a socket connected in a different
   * browser context which is busy with a visitor.
   */
  EXISTING_USER_SOCKET_BUSY = 'EXISTING_USER_SOCKET_BUSY',

  /**
   * Attempted to connect passively, but this user already
   * has an active connection.
   */
  PASSIVE_REJECTION = 'PASSIVE_REJECTION',

  /**
   * Socket was disconnected due to inactivity.
   */
  STALE = 'STALE',

  /**
   * Local timeout waiting for response from emit
   */
  ACTION_RESPONSE_TIMEOUT = 'ACTION_RESPONSE_TIMEOUT',

  /**
   * When an existing socket is already connected and actively on a call.
   */
  BUSY_CONNECTION = 'BUSY_CONNECTION',

  /**
   * Indicates the connection was considered disconnected on the server, treat
   * similarly to a DISPLACED error code
   */
  CONNECTION_DATA_NOT_FOUND = 'CONNECTION_DATA_NOT_FOUND',

  /**
   * Monthly limit for this operation has been reached
   **/
  LIMIT_REACHED = 'LIMIT_REACHED',
}

/** Common websocket emit actions */
export enum SbSocketEmitAction {
  pong = 'pong',
  subscribe = 'subscribe',
  unsubscribe = 'unsubscribe',
}

/** Common websocket absorb actions */
export enum SbSocketAbsorbAction {
  disconnecting = 'disconnecting',
  connectError = 'connect_error',
  ping = '_ping',
  actionResponse = 'action_response',
}

export enum _SbSocketOnEventName {
  status = 'status',
}
export type SbSocketOnEventName = _SbSocketOnEventName | SbSocketAbsorbAction;
export const SbSocketOnEventName = { ..._SbSocketOnEventName, ...SbSocketAbsorbAction };

export type SbSocketConstructArgsBase = {
  protocolVersion: number;
  logger: SbLoggerI;
  url: string;
  reconnectWhenOffline: boolean;
};
export type SbSocketConstructArgs<T extends Record<string, string>> =
  SbSocketConstructArgsBase & T;

export interface SbSocketConnectArgsBase {
  reconnectReset?: boolean;
}

export type SbSocketStatus = 'disconnected' | 'connecting' | 'connected' | 'shutdown';

/** Websocket emit message shape */
export type SbSocketEmitMessage = {
  action: string;
  payload?: Object;
  actionId?: number;
};

/** Websocket on event message shape */
export type SbSocketAbsorbMessage<E> = {
  actionId: number;
  action: keyof E;
  payload: Object;
  error: string;
  failedAction?: keyof E;
};

/** Promise returned from an emit */
type SbSocketEmitPromise<EmitEvent> = {
  res: (msg: SbSocketAbsorbMessage<EmitEvent>) => void;
  rej: (x: any) => void;
};

/** Emit event argument and response definition */
export interface SbSocketEmitEventDefinition {
  args?: any;
  response?: any;
}

/** Websocket emit events to server, their args and expected responses */
export interface SbSocketEmitEvent extends Record<string, SbSocketEmitEventDefinition> {
  [SbSocketEmitAction.pong]: {};
}
/** Websocket unsolicited events from server */
export interface SbSocketAbsorbEvent {
  [SbSocketAbsorbAction.ping]: void;
}

export type SbSocketStatusPackage = {
  status: SbSocketStatus;
  disconnectCode: SbSocketErrorCode | null;
};

/** On events from server (absorb) and locally generated (status) */
export interface SbSocketOnEvent extends SbSocketAbsorbEvent {
  [SbSocketOnEventName.status]: SbSocketStatusPackage;
}

export abstract class SbSocketAbstract<
  ConstructArgs extends SbSocketConstructArgsBase,
  ConnectArgs extends SbSocketConnectArgsBase | void,
  OnEvent extends SbSocketOnEvent,
  EmitEvent extends SbSocketEmitEvent,
  > {
  protected eventEmitter = new EventEmitter();
  #url: string;
  #protocolVersion: number;
  protected log: SbLoggerI;
  #status: SbSocketStatus;
  #ws: WebSocket | null;

  #actionPromises: Map<string, SbSocketEmitPromise<EmitEvent>> = new Map();
  #actionCount = 0;
  #chattyActions = new Set<keyof EmitEvent | keyof OnEvent>();

  protected connectArgs: undefined | ConnectArgs = undefined;
  #disconnectingCode: null | SbSocketErrorCode = null;
  #reconnectDelay = SbSocketAbstract.calculateDelay(); // initial delay
  #reconnectAttempts = 0;
  #reconnectTimeout = 0;
  #pongerTimeout = 0;
  #isReconnectWhenOffline = false;

  constructor({ logger, protocolVersion, url, reconnectWhenOffline }: ConstructArgs) {
    this.log = logger;
    this.#protocolVersion = protocolVersion;
    this.#url = url;
    this.#status = 'disconnected';
    this.#ws = null;
    this.#isReconnectWhenOffline = reconnectWhenOffline;
  }

  static calculateDelay(currentDelay?: number) {
    if (!currentDelay) {
      currentDelay = RECONNECT_DELAY / 2;
    }
    // limit delay to max
    let nextDelay = Math.min(currentDelay * 2, RECONNECT_DELAY_MAX);
    // jitter delay by +/- 50%
    nextDelay = Math.round(nextDelay - nextDelay / 2 + Math.random() * nextDelay);
    return nextDelay;
  }

  public connect(args?: ConnectArgs) {
    if (args?.reconnectReset !== false) {
      this.resetReconnect();
      if (args?.reconnectReset) {
        delete args.reconnectReset;
      }
    }
    const noArgs = {} as ConnectArgs;
    this.connectArgs = args || this.connectArgs || noArgs;
    this.destroy();

    const url = new URL(this.#url);
    Object.entries(this.makeConnectQuery()).forEach(([k, v]) => {
      // add some runtime type safety since we are seeing some issues here
      if (['number', 'string', 'boolean'].includes(typeof v)) {
        url.searchParams.append(k, v.toString());
      } else {
        this.log.warn(
          `Found a bad socket connect query parameter ${k}: ${v} (${typeof v})`,
        );
      }
    });
    this.#ws = new WebSocket(url);
    this.#setupWebsocket();
  }

  protected makeConnectQuery(): Record<string, number | string | boolean> {
    return {
      protocolVersion: this.#protocolVersion,
    };
  }

  protected addChattyAction(action: keyof EmitEvent | keyof OnEvent) {
    this.#chattyActions.add(action);
  }

  public disconnect(disconnectingCode?: SbSocketErrorCode) {
    this.destroy(disconnectingCode);
  }

  protected destroy(disconnectingCode?: SbSocketErrorCode) {
    if (!this.#ws) {
      return;
    }
    this.stopPonger();
    this.#disconnectingCode = disconnectingCode || null;
    this.#failAllActionPromises(this.#disconnectingCode || SbSocketErrorCode.UNKNOWN);
    this.#actionCount = 0;
    this.#ws.onerror = null;
    this.#ws.onopen = null;
    this.#ws.onmessage = null;
    const isClosed = this.#ws.readyState === WebSocket.CLOSED;
    if (!isClosed) {
      this.#ws.close(WS_CLOSE_CODE.NORMAL, 'client');
    }
    // save onclose handler to handle the close
    // this.#ws.onclose = null;
    this.#ws = null;
  }

  public getSocket() {
    return this.#ws;
  }

  public getStatus() {
    return this.#status;
  }

  public on<T extends keyof OnEvent>(eventName: T, cb: (data: OnEvent[T]) => void) {
    this.eventEmitter.on(eventName as string, cb);
  }

  public async emit<T extends keyof EmitEvent>(
    action: T,
    payload?: EmitEvent[T]['args'],
    noResponse?: boolean,
  ) {
    return new Promise<EmitEvent[T]['response']>((res, rej) => {
      // TODO - timeout if !noResponse
      if (this.#ws?.readyState === WebSocket.OPEN) {
        const data: SbSocketEmitMessage = { action: action as string, payload };
        if (!noResponse) {
          data.actionId = this.#registerActionPromise({ res, rej }, action, payload);
        }
        this.#ws?.send(JSON.stringify(data));
        if (noResponse) {
          res(undefined);
        }
      } else {
        throw new Error(`Tried to emit action [${action}], but WebSocket not open`);
      }
    });
  }

  protected changeStatus(status: SbSocketStatus) {
    this.#status = status;
    this.eventEmitter.emit(SbSocketOnEventName.status, {
      status: this.#status,
      disconnectCode: this.#disconnectingCode,
    });
  }

  protected wsOnCloseHandler(event: CloseEvent) {
    // 1006 is returned when WebSocket is unable to reach the server
    // and is the error we get when the server rejects apigw $connect
    // 4999 is a custom code we can use for testing
    if (event.code === 1006 || event.code === 4999) {
      if (navigator.onLine) {
        // Setting this code will ensure a reconnect is not attempted
        this.#disconnectingCode = SbSocketErrorCode.CONNECT_REJECTED;
      } else {
        this.#disconnectingCode = SbSocketErrorCode.OFFLINE;
        if (this.#isReconnectWhenOffline) {
          this.log.debug('Connection failed because we are offline, will retry soon');
          this.#attemptReconnect();
          return;
        }
      }
    }
    if (event.code > 1000 && event.code !== 1006 && event.code !== 4999) {
      // there was an external issue (server, network)
      this.log.debug(`WS closed with non-normal code [${event.code}], reconnecting...`);
      this.#attemptReconnect();
      return;
    }

    this.resetReconnect();

    this.log.debug(
      `WS close, ws code [${event.code}], ws reason [${
        event.reason
      }], disconnectingCode [${this.#disconnectingCode}]`,
    );

    // fail all action promises
    this.#failAllActionPromises(this.#disconnectingCode);

    this.changeStatus('disconnected');
    this.stopPonger();
  }

  protected wsOnOpenHandler() {
    this.log.debug('WebSocket.onopen');
    this.resetReconnect();
    this.changeStatus('connected');
    this.startPonger();
  }

  protected wsOnErrorHandler(error: Event) {
    // log this, but we only care about onclose (disconnect)
    // errors which this will trigger if it is a disconnect
    this.log.error('WebSocket.onerror', error);
  }

  protected wsOnMessageHandler(m: MessageEvent) {
    const data = JSON.parse(m.data || '{}') as SbSocketAbsorbMessage<OnEvent>;

    // External errors (apigw, cloudflare, &etc...)
    const apigwError = (data as { message?: string }).message || '';
    const possibleApigwErrors = ['Internal server error', 'Endpoint request timed out'];
    if (possibleApigwErrors.includes(apigwError)) {
      // Just log the error. If an introduction has been interrupted by
      // this, its promise will eventually time out and a reconnect
      // should be attempted if appropriate
      this.log.warn(`Server returned an error message [${apigwError}]`);
    }

    // BE generated responses
    const action = data.action || '';
    const payload = data.payload || {};
    const { error, actionId, failedAction } = data;

    if (error) {
      this.log.warn(`Ws error response code [${error}], action [${failedAction}]`);
      if (actionId) {
        this.absorbEmitFailedMessage(data as unknown as SbSocketAbsorbMessage<EmitEvent>);
      }
      return;
    }

    if (!this.#chattyActions.has(action)) {
      this.log.debug(`On message action [${action}]`);
    }

    if (action === SbSocketAbsorbAction.actionResponse) {
      const actionId = data.actionId || null;
      if (actionId) {
        this.#absorbEmitMessage(actionId, payload);
      } else {
        this.log.warn(`Received an action_response without an actionId`, { payload });
      }
    } else if (action === SbSocketAbsorbAction.disconnecting) {
      const _code = payload && (payload as { code: string }).code;
      if (!_code) {
        this.log.warn(`Expected disconnecting event to have a 'code' field`, data);
      }
      this.#disconnectingCode = this.resolveSocketError(_code);
    } else if (this.#hasWsListener(action)) {
      // simple pass-through events keyed on whatever listeners we have registered via .on(...)
      this.eventEmitter.emit(action as string, payload);
    } else {
      this.log.warn(`Received action for which there is no handler [${action}]`);
    }
  }

  #setupWebsocket() {
    if (!this.#ws) {
      throw new Error('Cannot setupWebsocket without WebSocket instance');
    }
    this.changeStatus('connecting');

    this.#ws.onclose = this.wsOnCloseHandler.bind(this);
    this.#ws.onerror = this.wsOnErrorHandler.bind(this);
    this.#ws.onopen = this.wsOnOpenHandler.bind(this);
    this.#ws.onmessage = this.wsOnMessageHandler.bind(this);
  }

  #hasWsListener(action: keyof OnEvent | keyof EmitEvent) {
    return this.eventEmitter.eventNames().includes(action as string);
  }

  #attemptReconnect() {
    clearTimeout(this.#reconnectTimeout);
    const reconnect = () => {
      if (!this.connectArgs) {
        throw new Error(
          `Cannot reconnect without connect arguments, has there been an initial 'connect(...)' called?`,
        );
      }
      this.connect({ ...this.connectArgs, reconnectReset: false } as ConnectArgs);
    };
    this.#reconnectAttempts++;
    this.log.debug(
      `Will attempt reconnect #${this.#reconnectAttempts} in ${this.#reconnectDelay}ms`,
    );
    this.#reconnectTimeout = window.setTimeout(reconnect, this.#reconnectDelay);
    this.#reconnectDelay = SbSocketAbstract.calculateDelay(this.#reconnectDelay);
  }

  protected resetReconnect() {
    clearTimeout(this.#reconnectTimeout);
    this.#reconnectDelay = SbSocketAbstract.calculateDelay();
    this.#reconnectAttempts = 0;
  }

  #registerActionPromise<T extends keyof EmitEvent>(
    promise: SbSocketEmitPromise<EmitEvent>,
    action: T,
    payload?: EmitEvent[T]['args'],
  ) {
    const actionId = ++this.#actionCount;
    this.#actionPromises.set(actionId.toString(), promise);
    setTimeout(() => {
      if (this.#actionPromises.has(actionId.toString())) {
        this.log.debug(`Emit action ${action} timed out (actionId=${actionId})`);
        this.absorbEmitFailedMessage({
          actionId,
          action,
          payload: payload || {},
          error: SbSocketErrorCode.ACTION_RESPONSE_TIMEOUT,
        });
      }
    }, ACTION_RESPONSE_TIMEOUT);
    return actionId;
  }

  #absorbEmitMessage(actionId: number, payload?: Object) {
    const promiseHandlers = this.#actionPromises.get(actionId.toString());
    if (promiseHandlers) {
      promiseHandlers.res(payload as SbSocketAbsorbMessage<EmitEvent>);
    } else {
      this.log.warn(`No callback found for actionId [${actionId}]`);
    }
    this.#clearActionPromise(actionId);
  }

  protected absorbEmitFailedMessage(failed: SbSocketAbsorbMessage<EmitEvent>) {
    const promiseHandlers = this.#actionPromises.get(failed.actionId.toString());
    if (promiseHandlers) {
      promiseHandlers.rej(failed.error);
    }
    this.#clearActionPromise(failed.actionId);
  }

  #clearActionPromise(actionId: number) {
    this.#actionPromises.delete(actionId.toString());
  }

  #failAllActionPromises(reason?: SbSocketErrorCode | null) {
    this.#actionPromises.forEach(({ rej }, _) => {
      rej(reason || SbSocketErrorCode.UNKNOWN);
    });
    this.#actionPromises.clear();
  }

  resolveSocketError(error: string) {
    let code = Object.values(SbSocketErrorCode).includes(error as SbSocketErrorCode)
      ? (error as SbSocketErrorCode)
      : SbSocketErrorCode.UNKNOWN;

    if (code === SbSocketErrorCode.UNKNOWN) {
      this.log.warn(`Received unexpected socket error code [${error}]`);
    }
    // no code means it was probably client initiated
    if (!error) {
      code = SbSocketErrorCode.CLIENT_DISCONNECT;
    }
    return code;
  }

  protected startPonger() {
    this.log.debug('start pong');
    const jitter = Math.round(PONG_FREQUENCY * 0.2);
    const lower = PONG_FREQUENCY - jitter;
    const nextTime = () => lower + Math.random() * jitter * 2;
    const pong = () => {
      void this.emit(SbSocketEmitAction.pong, undefined, true);
      this.#pongerTimeout = window.setTimeout(pong.bind(this), nextTime());
    };

    this.#pongerTimeout = window.setTimeout(pong, nextTime());
  }
  protected stopPonger() {
    if (this.#pongerTimeout) {
      this.log.debug('stop pong');
      clearTimeout(this.#pongerTimeout);
      this.#pongerTimeout = 0;
    }
  }
}
